import { arrayMove } from "@dnd-kit/sortable";
import { createId } from "@paralleldrive/cuid2";
import { PlusIcon } from "@phosphor-icons/react";
import { defaultSections, type Education } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import get from "lodash.get";
import { useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useResumeStore } from "@/client/stores/resume";
import { formatEducationPeriod } from "./education/date-utils";
import { EducationForm } from "./education/education-form";
import { EducationList } from "./education/education-list";

type View = "list" | "form";
type EducationSection = { items: Education[] };

export const EducationInlineSection = () => {
  const { toast } = useToast();
  const setValue = useResumeStore((state) => state.setValue);
  const section = useResumeStore(
    (state) =>
      get(state.resume.data.sections, "education", defaultSections.education) as EducationSection,
  );
  const items = section.items ?? [];
  const [view, setView] = useState<View>(() => (items.length ? "list" : "form"));
  const [editing, setEditing] = useState<Education>();
  const add = () => {
    setEditing(undefined);
    setView("form");
  };
  const edit = (item: Education) => {
    setEditing(item);
    setView("form");
  };
  const cancel = () => {
    setEditing(undefined);
    setView(items.length ? "list" : "form");
  };
  const save = (values: Education) => {
    const payload: Education = {
      ...values,
      id: editing?.id ?? createId(),
      visible: editing?.visible ?? true,
      date: formatEducationPeriod(
        values.startDate ?? "",
        values.endDate ?? "",
        Boolean(values.isPresent),
      ),
    };
    setValue(
      "sections.education.items",
      editing
        ? items.map((item) => (item.id === editing.id ? payload : item))
        : [...items, payload],
    );
    setEditing(undefined);
    setView("list");
    toast({ variant: "success", title: editing ? "Formation mise à jour" : "Formation ajoutée" });
  };
  const remove = (id: string) => {
    setValue(
      "sections.education.items",
      items.filter((item) => item.id !== id),
    );
    toast({ variant: "success", title: "Formation supprimée" });
  };
  const reorder = (active: string, over: string) => {
    const from = items.findIndex((item) => item.id === active);
    const to = items.findIndex((item) => item.id === over);
    if (from >= 0 && to >= 0) setValue("sections.education.items", arrayMove(items, from, to));
  };

  return (
    <section id="education" className="mx-auto w-full max-w-[720px] pb-3">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
            Formation
          </p>
          <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-indigo-950 sm:text-[28px]">
            Votre parcours académique
          </h1>
          <p className="mt-1.5 max-w-lg text-xs leading-5 text-slate-500 sm:text-sm">
            Présentez les formations qui renforcent directement votre candidature.
          </p>
        </div>
        {view === "list" && (
          <button
            type="button"
            onClick={add}
            className="inline-flex min-h-10 w-fit items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800"
          >
            <PlusIcon size={15} weight="bold" /> Ajouter
          </button>
        )}
      </header>
      <div role="tablist" className="mt-5 flex rounded-xl bg-slate-100 p-1">
        <button
          type="button"
          role="tab"
          aria-selected={view === "list"}
          disabled={!items.length}
          onClick={() => setView("list")}
          className={cn(
            "min-h-9 flex-1 rounded-lg text-[11px] font-bold",
            view === "list" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500",
            !items.length && "opacity-40",
          )}
        >
          Formations ajoutées{" "}
          <span className="ml-1 rounded-full bg-indigo-50 px-1.5 py-0.5 text-[9px]">
            {items.length}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "form"}
          onClick={add}
          className={cn(
            "min-h-9 flex-1 rounded-lg text-[11px] font-bold",
            view === "form" ? "bg-white text-indigo-700 shadow-sm" : "text-slate-500",
          )}
        >
          {editing ? "Modification" : "Nouvelle formation"}
        </button>
      </div>
      <div className="mt-4">
        {view === "form" ? (
          <EducationForm item={editing} onCancel={cancel} onSave={save} />
        ) : (
          <EducationList
            items={items}
            onAdd={add}
            onEdit={edit}
            onDelete={remove}
            onReorder={reorder}
          />
        )}
      </div>
    </section>
  );
};
